import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { currentDatabase } from "@/local/database";
import * as outbox from "@/local/outbox";
import * as local from "@/local/repo";
import { requestSync } from "@/local/sync";
import type { SqlDriver } from "@/local/types";
import { api, ApiError, type Envelope } from "./api";
import { loadLastUser, saveLastUser } from "./lastUser";
import type {
  AccountReport,
  AccountType,
  Activity,
  BodyMeasurement,
  Breakdown,
  BudgetOverview,
  CashFlow,
  Dashboard,
  DuplicatePair,
  Exercise,
  ExerciseHistory,
  FitnessDashboard,
  FitnessHistory,
  FitnessProgress,
  Goal,
  ImportBatch,
  ImportPreset,
  ImportRow,
  IncomeVsExpenses,
  MonthlySummary,
  NetWorth,
  NetWorthHistory,
  Reconciliation,
  Session,
  TemplateExerciseInput,
  Transaction,
  TransactionDetail,
  User,
  Workout,
  WorkoutSetInput,
  WorkoutTemplate,
} from "./types";

export const keys = {
  me: ["me"] as const,
  accounts: ["accounts"] as const,
  accountTypes: ["account-types"] as const,
  categories: ["categories"] as const,
  merchants: (q: string) => ["merchants", q] as const,
  transactions: (params: Record<string, string>) => ["transactions", params] as const,
  transaction: (id: string) => ["transaction", id] as const,
  dashboard: (month: string) => ["dashboard", month] as const,
  budgets: (month: string) => ["budgets", month] as const,
  report: (kind: string, params: Record<string, string>) => ["reports", kind, params] as const,
  netWorth: ["net-worth"] as const,
  imports: ["imports"] as const,
  importBatch: (id: string) => ["import", id] as const,
  importRows: (id: string, status: string) => ["import-rows", id, status] as const,
  reconciliations: (accountId: string) => ["reconciliations", accountId] as const,
  reconciliation: (id: string) => ["reconciliation", id] as const,
  duplicates: ["duplicates"] as const,
  sessions: ["sessions"] as const,
  outbox: ["outbox"] as const,
  fitnessDashboard: ["fitness", "dashboard"] as const,
  fitnessExercises: (includeInactive: boolean) => ["fitness", "exercises", includeInactive] as const,
  fitnessExerciseHistory: (id: string, params: Record<string, string>) =>
    ["fitness", "exercise-history", id, params] as const,
  fitnessWorkouts: (params: Record<string, string>) => ["fitness", "workouts", params] as const,
  fitnessActivities: (params: Record<string, string>) => ["fitness", "activities", params] as const,
  fitnessGoals: (includeInactive: boolean) => ["fitness", "goals", includeInactive] as const,
  fitnessTemplates: ["fitness", "templates"] as const,
  fitnessBodyMeasurements: (params: Record<string, string>) =>
    ["fitness", "body-measurements", params] as const,
  fitnessHistory: (params: Record<string, string>) => ["fitness", "history", params] as const,
  fitnessProgress: (params: Record<string, string>) => ["fitness", "progress", params] as const,
};

/** Everything Fitness reads: re-fetch after any Fitness write (create/update/delete). */
function invalidateFitness(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ["fitness"] });
}

/** Anything that moves money can change every derived number: refetch them all. */
export function invalidateLedger(qc: QueryClient) {
  for (const key of [
    keys.me, keys.accounts, ["transactions"], ["transaction"], ["dashboard"], ["budgets"], ["reports"],
    keys.netWorth, ["reconciliations"], ["reconciliation"], keys.duplicates, keys.imports,
    ["import"], ["import-rows"], keys.categories, ["merchants"],
  ]) {
    void qc.invalidateQueries({ queryKey: key });
  }
}

/** Everything that reads from the device database: re-read after the database changed. */
export function invalidateLocal(qc: QueryClient) {
  for (const key of [keys.accounts, keys.categories, ["merchants"], ["transactions"], ["transaction"], keys.outbox]) {
    void qc.invalidateQueries({ queryKey: key });
  }
}

export const isNetworkError = (error: unknown): boolean =>
  error instanceof ApiError && (error.status === 0 || error.code === "NETWORK_ERROR");

/**
 * Who am I? Asks the server, but a missing network is not "signed out": fall back to the last
 * known user so the app opens offline. A 401 (or any other server answer) is still authoritative.
 * `networkMode: "always"` because TanStack Query would otherwise never even run this offline.
 */
export const useMe = () =>
  useQuery({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        const user = await api.get<User>("/me");
        saveLastUser(user);
        return user;
      } catch (error) {
        const cached = isNetworkError(error) ? loadLastUser() : null;
        if (cached) return cached;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
    networkMode: "always",
  });

/**
 * What the sign-in screens need before anyone is signed in (whether sign-up is open). The server
 * enforces it either way; this only keeps the UI from offering what will be refused.
 */
export const useAuthConfig = () =>
  useQuery({
    queryKey: ["auth-config"],
    queryFn: () => api.get<{ registration_open: boolean }>("/auth/config"),
    staleTime: 5 * 60_000,
    retry: false,
    networkMode: "always",
  });

// --- data that lives on the device -----------------------------------------------------------
//
// Accounts, categories, merchants and transactions are read from the local SQLite replica, which
// the sync engine keeps current. The hooks return the same shapes the REST API does, so screens
// don't know the difference. staleTime Infinity: a sync (or a mutation) invalidates them when the
// data actually changes; networkMode "always": they must work with no network at all.

function localDb() {
  const database = currentDatabase();
  if (!database) throw new Error("Your ledger is still opening");
  return database.driver;
}

const LOCAL = { staleTime: Infinity, networkMode: "always" } as const;

export const useAccounts = () =>
  useQuery({ queryKey: keys.accounts, queryFn: () => local.listAccounts(localDb()), ...LOCAL });

export const useAccountTypes = () =>
  useQuery({ queryKey: keys.accountTypes, queryFn: () => api.get<AccountType[]>("/accounts/types"),
             staleTime: Infinity });

export const useCategories = () =>
  useQuery({ queryKey: keys.categories, queryFn: () => local.listCategories(localDb()), ...LOCAL });

export const useMerchants = (q: string) =>
  useQuery({
    queryKey: keys.merchants(q),
    queryFn: () => local.searchMerchants(localDb(), q, 8),
    enabled: q.trim().length > 0,
    ...LOCAL,
  });

export function useTransactions(params: Record<string, string>) {
  return useInfiniteQuery({
    queryKey: keys.transactions(params),
    queryFn: async ({ pageParam }): Promise<Envelope<Transaction[]>> => {
      const { rows, nextOffset } = await local.listTransactions(localDb(), params, {
        limit: 50,
        offset: pageParam,
      });
      return { data: rows, meta: { request_id: "", next_cursor: nextOffset === null ? null : String(nextOffset) } };
    },
    initialPageParam: 0,
    getNextPageParam: (last) => (last.meta.next_cursor ? Number(last.meta.next_cursor) : undefined),
    placeholderData: keepPreviousData,
    ...LOCAL,
  });
}

export const useTransaction = (id: string | null) =>
  useQuery({
    queryKey: keys.transaction(id ?? ""),
    queryFn: async (): Promise<TransactionDetail> => {
      const detail = await local.getTransactionDetail(localDb(), id ?? "");
      if (!detail) throw new ApiError(404, "NOT_FOUND", "Transaction not found");
      return detail;
    },
    enabled: Boolean(id),
    ...LOCAL,
  });

/** How many changes are waiting to reach the server, and how many it refused. */
export const useOutboxSummary = () =>
  useQuery({ queryKey: [...keys.outbox, "summary"], queryFn: () => outbox.outboxSummary(localDb()), ...LOCAL });

/** The changes the server refused, with the reason and what the user had entered. */
export const useAttention = () =>
  useQuery({ queryKey: [...keys.outbox, "attention"], queryFn: () => outbox.listAttention(localDb()), ...LOCAL });

export const useDashboard = (month: string) =>
  useQuery({
    queryKey: keys.dashboard(month),
    queryFn: () => api.get<Dashboard>("/dashboard", { month }),
    placeholderData: keepPreviousData,
  });

export const useBudgets = (month: string) =>
  useQuery({
    queryKey: keys.budgets(month),
    queryFn: () => api.get<BudgetOverview>("/budgets", { month }),
    placeholderData: keepPreviousData,
  });

export const useIncomeVsExpenses = (params: Record<string, string>) =>
  useQuery({
    queryKey: keys.report("income-vs-expenses", params),
    queryFn: () => api.get<IncomeVsExpenses>("/reports/income-vs-expenses", params),
    placeholderData: keepPreviousData,
  });

export const useBreakdown = (kind: "expenses" | "income", params: Record<string, string>) =>
  useQuery({
    queryKey: keys.report(kind, params),
    queryFn: () => api.get<Breakdown>(`/reports/${kind}`, params),
    placeholderData: keepPreviousData,
  });

export const useCashFlow = (params: Record<string, string>) =>
  useQuery({
    queryKey: keys.report("cash-flow", params),
    queryFn: () => api.get<CashFlow>("/reports/cash-flow", params),
    placeholderData: keepPreviousData,
  });

export const useMonthlySummary = (month: string) =>
  useQuery({
    queryKey: keys.report("monthly-summary", { month }),
    queryFn: () => api.get<MonthlySummary>("/reports/monthly-summary", { month }),
    placeholderData: keepPreviousData,
  });

export const useAccountReport = (id: string, params: Record<string, string>) =>
  useQuery({
    queryKey: keys.report(`account-${id}`, params),
    queryFn: () => api.get<AccountReport>(`/reports/accounts/${id}`, params),
    placeholderData: keepPreviousData,
  });

export const useNetWorth = () =>
  useQuery({
    queryKey: [...keys.netWorth, "now"],
    queryFn: () => api.get<NetWorth>("/net-worth"),
  });

export const useNetWorthHistory = () =>
  useQuery({
    queryKey: keys.netWorth,
    queryFn: () => api.get<NetWorthHistory>("/net-worth/history", { months: 12 }),
  });

export const useImportPresets = () =>
  useQuery({ queryKey: ["import-presets"], queryFn: () => api.get<ImportPreset[]>("/imports/presets"),
             staleTime: Infinity });

export const useImports = () =>
  useQuery({ queryKey: keys.imports, queryFn: () => api.get<ImportBatch[]>("/imports") });

export const useImportBatch = (id: string) =>
  useQuery({ queryKey: keys.importBatch(id), queryFn: () => api.get<ImportBatch>(`/imports/${id}`) });

export const useImportRows = (id: string, status: string) =>
  useQuery({
    queryKey: keys.importRows(id, status),
    queryFn: () => api.get<ImportRow[]>(`/imports/${id}/rows`, { status, limit: 500 }),
    placeholderData: keepPreviousData,
  });

export const useReconciliations = (accountId: string) =>
  useQuery({
    queryKey: keys.reconciliations(accountId),
    queryFn: () => api.get<Reconciliation[]>("/reconciliations", { account_id: accountId }),
  });

export const useDuplicates = () =>
  useQuery({ queryKey: keys.duplicates, queryFn: () => api.get<DuplicatePair[]>("/duplicates") });

export const useSessions = () =>
  useQuery({ queryKey: keys.sessions, queryFn: () => api.get<Session[]>("/auth/sessions") });

// --- writing transactions: to the device first, then to the server ---------------------------------
//
// A transaction is saved to the device database (and the outbox) at once, so it works with no
// network. If the phone is online the server is given a moment to answer, which keeps the old
// behaviour: a refusal (a refund over the original amount, ...) shows on the form, where the user
// can fix it. If there is no answer in time - or no network - it is simply "saved, will sync".

/** How long a save waits for the server before settling for "saved on this device". */
const SETTLE_MS = 4000;

export interface WriteOutcome {
  /** True when the server has not confirmed it yet (it will, automatically). */
  queued: boolean;
}

async function settle(db: SqlDriver, opId: string | null): Promise<WriteOutcome> {
  if (opId === null) return { queued: false };
  if (!navigator.onLine) {
    void requestSync();
    return { queued: true };
  }
  await Promise.race([requestSync(), new Promise<void>((resolve) => setTimeout(resolve, SETTLE_MS))]);
  const op = await outbox.getOp(db, opId);
  if (!op || op.status === "acked") return { queued: false }; // confirmed (and already cleared)
  if (op.status === "conflict" || op.status === "rejected") {
    // Still on the form: hand the reason back so the user can act on it there, and drop the
    // refused attempt (the device already holds the server's copy).
    await outbox.discardOp(db, opId);
    throw new ApiError(op.status === "conflict" ? 409 : 422, op.error_code ?? "REJECTED",
                       op.error_message ?? "The server did not accept this change.", { fields: op.error_fields });
  }
  return { queued: true };
}

function useLocalWrite<TVars>(record: (db: SqlDriver, vars: TVars) => Promise<outbox.Enqueued>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: TVars): Promise<WriteOutcome> => {
      const db = localDb();
      const { opId } = await record(db, vars);
      invalidateLocal(qc); // the screens already show it
      return settle(db, opId);
    },
    onSettled: () => invalidateLocal(qc),
    // Totals, budgets and reports are the server's: refresh them once it has taken the change.
    onSuccess: (outcome) => {
      if (!outcome.queued) invalidateLedger(qc);
    },
  });
}

export interface CreateTransactionVars {
  /** Chosen once per form, so submitting twice can only ever record one transaction. */
  id: string;
  input: outbox.TransactionInput;
}
export const useCreateTransaction = () =>
  useLocalWrite((db, v: CreateTransactionVars) => outbox.enqueueCreate(db, v.id, crypto.randomUUID(), v.input));

export interface UpdateTransactionVars {
  id: string;
  changes: outbox.TransactionChanges;
  /** The version the form was opened on; a newer local copy means someone else's edit arrived. */
  expectedVersion: number;
}
export const useUpdateTransaction = () =>
  useLocalWrite((db, v: UpdateTransactionVars) =>
    outbox.enqueueUpdate(db, v.id, crypto.randomUUID(), v.changes, v.expectedVersion));

export interface VoidTransactionVars {
  id: string;
  reason: string | null;
}
export const useVoidTransaction = () =>
  useLocalWrite((db, v: VoidTransactionVars) => outbox.enqueueVoid(db, v.id, crypto.randomUUID(), v.reason));

/**
 * Mutation that refreshes every derived figure on success. The change has just been accepted by
 * the server, so pull it into the device replica first: the lists read locally and would
 * otherwise keep showing the old data until the next scheduled sync.
 */
export function useLedgerMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await requestSync();
      invalidateLedger(qc);
    },
  });
}

// --- fitness -----------------------------------------------------------------------------
//
// Online-only, like Budgets/Dashboard/Reports: read and written straight from /api/v1/fitness,
// not through the device replica. The server's pull-sync feed does carry Fitness entities now
// (see local/repo.ts's SyncChange comment), so a future version of this app could read them
// offline too - but this version has nowhere local to put them, so every screen here needs a
// connection, the same trade-off Budgets already makes.

export const useFitnessDashboard = () =>
  useQuery({
    queryKey: keys.fitnessDashboard,
    queryFn: () => api.get<FitnessDashboard>("/fitness/dashboard"),
  });

export const useExercises = (includeInactive = true) =>
  useQuery({
    queryKey: keys.fitnessExercises(includeInactive),
    queryFn: () => api.get<Exercise[]>("/fitness/exercises", { include_inactive: includeInactive }),
  });

export const useExerciseHistory = (id: string, params: Record<string, string>) =>
  useQuery({
    queryKey: keys.fitnessExerciseHistory(id, params),
    queryFn: () => api.get<ExerciseHistory>(`/fitness/exercises/${id}/history`, params),
    enabled: Boolean(id),
  });

export const useWorkouts = (params: Record<string, string> = {}) =>
  useQuery({
    queryKey: keys.fitnessWorkouts(params),
    queryFn: () => api.get<Workout[]>("/fitness/workouts", params),
    placeholderData: keepPreviousData,
  });

export const useActivities = (params: Record<string, string> = {}) =>
  useQuery({
    queryKey: keys.fitnessActivities(params),
    queryFn: () => api.get<Activity[]>("/fitness/activities", params),
    placeholderData: keepPreviousData,
  });

export const useGoals = (includeInactive = true) =>
  useQuery({
    queryKey: keys.fitnessGoals(includeInactive),
    queryFn: () => api.get<Goal[]>("/fitness/goals", { include_inactive: includeInactive }),
  });

export const useTemplates = () =>
  useQuery({
    queryKey: keys.fitnessTemplates,
    queryFn: () => api.get<WorkoutTemplate[]>("/fitness/templates"),
  });

export const useBodyMeasurements = (params: Record<string, string> = {}) =>
  useQuery({
    queryKey: keys.fitnessBodyMeasurements(params),
    queryFn: () => api.get<BodyMeasurement[]>("/fitness/body-measurements", params),
    placeholderData: keepPreviousData,
  });

export const useFitnessHistory = (params: Record<string, string>) =>
  useQuery({
    queryKey: keys.fitnessHistory(params),
    queryFn: () => api.get<FitnessHistory>("/fitness/history", params),
    placeholderData: keepPreviousData,
  });

export const useFitnessProgress = (params: Record<string, string>) =>
  useQuery({
    queryKey: keys.fitnessProgress(params),
    queryFn: () => api.get<FitnessProgress>("/fitness/progress", params),
    placeholderData: keepPreviousData,
  });

/**
 * Mutation that refetches every Fitness screen on success - the same idea as useLedgerMutation,
 * scoped to Fitness's own query keys since Fitness has no device replica to pull into.
 */
export function useFitnessMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateFitness(qc) });
}

export interface VersionedFitnessEdit {
  id: string;
  version: number;
  data: Record<string, unknown>;
}

// exercises

export interface ExerciseInput {
  name: string;
  muscle_group: string;
  equipment?: string | null;
  instructions?: string | null;
  notes?: string | null;
}

export const useCreateExercise = () =>
  useFitnessMutation((input: ExerciseInput) => api.post<Exercise>("/fitness/exercises", input));

export const useUpdateExercise = () =>
  useFitnessMutation((vars: VersionedFitnessEdit) =>
    api.patch<Exercise>(`/fitness/exercises/${vars.id}`, vars.data, vars.version));

export const useDeleteExercise = () =>
  useFitnessMutation((id: string) => api.delete(`/fitness/exercises/${id}`));

// workouts

export interface WorkoutInput {
  date: string;
  workout_type: string;
  duration_minutes: number;
  notes?: string | null;
  template_id?: string | null;
  sets: WorkoutSetInput[];
}

export const useCreateWorkout = () =>
  useFitnessMutation((input: WorkoutInput) => api.post<Workout>("/fitness/workouts", input));

export const useUpdateWorkout = () =>
  useFitnessMutation((vars: VersionedFitnessEdit) =>
    api.patch<Workout>(`/fitness/workouts/${vars.id}`, vars.data, vars.version));

export const useDeleteWorkout = () =>
  useFitnessMutation((id: string) => api.delete(`/fitness/workouts/${id}`));

// walking / running activities

export interface ActivityInput {
  activity_type: string;
  date: string;
  distance_km: string;
  duration_minutes: number;
  calories?: number | null;
  notes?: string | null;
}

export const useCreateActivity = () =>
  useFitnessMutation((input: ActivityInput) => api.post<Activity>("/fitness/activities", input));

export const useUpdateActivity = () =>
  useFitnessMutation((vars: VersionedFitnessEdit) =>
    api.patch<Activity>(`/fitness/activities/${vars.id}`, vars.data, vars.version));

export const useDeleteActivity = () =>
  useFitnessMutation((id: string) => api.delete(`/fitness/activities/${id}`));

// goals

export interface GoalInput {
  name: string;
  metric: string;
  target_value: string;
  unit_label?: string | null;
  period: string;
  start_date: string;
}

export const useCreateGoal = () =>
  useFitnessMutation((input: GoalInput) => api.post<Goal>("/fitness/goals", input));

export const useUpdateGoal = () =>
  useFitnessMutation((vars: VersionedFitnessEdit) =>
    api.patch<Goal>(`/fitness/goals/${vars.id}`, vars.data, vars.version));

export const useDeleteGoal = () =>
  useFitnessMutation((id: string) => api.delete(`/fitness/goals/${id}`));

// templates

export interface TemplateInput {
  name: string;
  default_duration_minutes?: number | null;
  exercises: TemplateExerciseInput[];
}

export const useCreateTemplate = () =>
  useFitnessMutation((input: TemplateInput) => api.post<WorkoutTemplate>("/fitness/templates", input));

export const useUpdateTemplate = () =>
  useFitnessMutation((vars: VersionedFitnessEdit) =>
    api.patch<WorkoutTemplate>(`/fitness/templates/${vars.id}`, vars.data, vars.version));

export const useDeleteTemplate = () =>
  useFitnessMutation((id: string) => api.delete(`/fitness/templates/${id}`));

// body measurements

export interface BodyMeasurementInput {
  date: string;
  weight_kg: string;
  notes?: string | null;
}

export const useCreateBodyMeasurement = () =>
  useFitnessMutation((input: BodyMeasurementInput) =>
    api.post<BodyMeasurement>("/fitness/body-measurements", input));

export const useUpdateBodyMeasurement = () =>
  useFitnessMutation((vars: VersionedFitnessEdit) =>
    api.patch<BodyMeasurement>(`/fitness/body-measurements/${vars.id}`, vars.data, vars.version));

export const useDeleteBodyMeasurement = () =>
  useFitnessMutation((id: string) => api.delete(`/fitness/body-measurements/${id}`));
